/**
 * SidebarIcon — wspólny render ikony pozycji menu z kolorem per obszar (za wyłącznikiem
 * SIDEBAR_ICONS_COLORFUL). Składa w jedno ~16 rozjechanych `<Icon className="h-4 w-4">`
 * z Sidebar.tsx (świadoma duplikacja admin/tenant).
 *
 * Kolor idzie za ZNACZENIEM (grupą nawigacji), nie za pojedynczą ikoną:
 *  - tenant: kolor OBSZARU modułu (InstalledMenuArea.color z /api/my/installed-modules),
 *  - panel ITE: kolor GRUPY (Sprzedaż/Katalog/Operacje/System) — bo dane admina nie mają
 *    pojęcia „obszar" (statyczny, płaski ADMIN_MENU),
 *  - baza stała / pozycje systemowe / moduły bez obszaru: stały kolor po nazwie ikony
 *    (z hashową paletą zapasową, żeby ten sam moduł miał zawsze ten sam kolor).
 *
 * Stany błędu (moduł nieaktywny, niepoprawny URL) NIE są kolorowane — zostają szare.
 */
import type { CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';

import { sidebarIconClass, useIsSidebarCollapsed } from '@/components/sidebar-collapse';
import { SIDEBAR_ICONS_COLORFUL } from '@/config/ui';
import { useSidebarSkin } from '@/hooks/useSidebarSkin';

// Grupy nawigacji panelu ITE → kolor (przedłużenie języka koloru kafelków modułów).
const ADMIN_GROUP_COLOR = {
  sales: '#6366f1',
  catalog: '#0ea5e9',
  operations: '#f59e0b',
  system: '#8b5cf6',
} as const;
type AdminGroup = keyof typeof ADMIN_GROUP_COLOR;

const ADMIN_ITEM_GROUP: Record<string, AdminGroup> = {
  dashboard: 'operations',
  modules: 'catalog',
  'module-pricing': 'catalog',
  bundles: 'catalog',
  db_pools: 'catalog',
  db_servers: 'catalog',
  module_templates: 'catalog',
  kartoteki: 'catalog',
  global_document_types: 'catalog',
  global_dictionaries: 'catalog',
  chart_of_accounts_templates: 'catalog',
  tenants: 'sales',
  subscriptions: 'sales',
  invoices: 'sales',
  coupons: 'sales',
  admin_users: 'system',
  audit_log: 'system',
  menu_builder: 'system',
  process_templates: 'operations',
};

/** Kolor pozycji statycznego menu panelu ITE po jej kluczu (grupa → kolor). */
export function adminItemColor(key: string): string {
  return ADMIN_GROUP_COLOR[ADMIN_ITEM_GROUP[key] ?? 'system'];
}

const GROUP_COLORS = Object.values(ADMIN_GROUP_COLOR);

/**
 * Kolor SEKCJI w custom layoucie panelu ITE — wszystkie pozycje jednej sekcji dzielą
 * jeden kolor (grupowanie, nie tęcza per ikona). Znane nazwy grup mapowane wprost;
 * nieznane → stabilny hash na jeden z 4 kolorów grup (spójna, ograniczona paleta).
 */
export function adminSectionColor(label: string | null | undefined): string {
  const norm = (label ?? '').toLowerCase();
  if (norm.includes('sprzeda')) return ADMIN_GROUP_COLOR.sales;
  if (norm.includes('katalog')) return ADMIN_GROUP_COLOR.catalog;
  if (norm.includes('operac')) return ADMIN_GROUP_COLOR.operations;
  if (norm.includes('system')) return ADMIN_GROUP_COLOR.system;
  let h = 0;
  for (let i = 0; i < norm.length; i++) h = (h * 31 + norm.charCodeAt(i)) >>> 0;
  return GROUP_COLORS[h % GROUP_COLORS.length]!;
}

// Kolor po nazwie ikony (baza stała, pozycje systemowe, moduły/linki bez obszaru).
const ICON_COLOR: Record<string, string> = {
  LayoutDashboard: '#6366f1',
  Package: '#8b5cf6',
  BadgeDollarSign: '#10b981',
  Database: '#a855f7',
  Server: '#a855f7',
  LayoutTemplate: '#d946ef',
  Library: '#14b8a6',
  FileStack: '#3b82f6',
  ListTree: '#f97316',
  Landmark: '#78716c',
  Building2: '#06b6d4',
  Receipt: '#0ea5e9',
  FileText: '#f59e0b',
  Ticket: '#f43f5e',
  ShieldCheck: '#22c55e',
  ScrollText: '#eab308',
  GitBranch: '#ec4899',
  MenuSquare: '#64748b',
  Settings: '#64748b',
  UserCog: '#6366f1',
  BadgeCheck: '#16a34a',
  Briefcase: '#0891b2',
  Contact: '#7c3aed',
  Users: '#6366f1',
  FolderLock: '#f59e0b',
  Calendar: '#0ea5e9',
  CalendarOff: '#f43f5e',
};

const PALETTE = [
  '#6366f1',
  '#0ea5e9',
  '#f59e0b',
  '#8b5cf6',
  '#10b981',
  '#ec4899',
  '#14b8a6',
  '#f43f5e',
  '#a855f7',
  '#06b6d4',
];

/** Stabilny hash nazwy → indeks palety (ten sam moduł zawsze ten sam kolor). */
function hashColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

/** Kolor po nazwie ikony z zapasową paletą hashową. */
export function iconColorByName(name: string | null | undefined): string {
  if (!name) return '#64748b';
  return ICON_COLOR[name] ?? hashColor(name);
}

/**
 * Ikona pozycji menu. `color` = preferowany kolor (obszar/grupa); brak → kolor po nazwie
 * ikony. `muted` (stan błędu/nieaktywny) albo wyłączony tryb kolorowy → render jak dawniej
 * (szara ikona, bez kafelka).
 */
export function SidebarIcon({
  icon: Icon,
  iconName,
  color,
  muted,
  sizeClass,
}: {
  icon: LucideIcon;
  /** Nazwa ikony do zapasowego doboru koloru, gdy `color` nie podano. */
  iconName?: string | null;
  color?: string | null;
  muted?: boolean;
  /**
   * Override rozmiaru ikony (np. `h-5 w-5`) — używany w wierszach sidebara tenanta, gdzie
   * Figma ma większą ikonę (wektor 20px). Brak → domyślny `sidebarIconClass(collapsed)`.
   */
  sizeClass?: string;
}) {
  const collapsed = useIsSidebarCollapsed();
  const skin = useSidebarSkin();
  const sizeCls = sizeClass ?? sidebarIconClass(collapsed);

  // Skórka „antd" (praca UI/UX): ikony jak w projekcie Grzegorza — kształty lucide (team,
  // teczka, osoba+trybik, budynek, trybik, siatka), monochromatyczne (dziedziczą kolor
  // pozycji: jasne na niebieskim, białe gdy aktywna) i cieńsze (stroke 1.5). Bez kafelka.
  if (skin === 'antd') {
    return <Icon className={`${sizeCls} flex-shrink-0`} strokeWidth={1.2} aria-hidden="true" />;
  }

  if (!SIDEBAR_ICONS_COLORFUL || muted) {
    return <Icon className={`${sizeCls} flex-shrink-0`} aria-hidden="true" />;
  }

  const c = color ?? iconColorByName(iconName);
  return (
    <span className="sidebar-icon-tile" style={{ '--icon-c': c } as CSSProperties}>
      <Icon className={sizeCls} aria-hidden="true" />
    </span>
  );
}
